import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../services/firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { localAuth } from '../../services/localAuth';
import { isUserAdmin } from '../../utils/adminUtils';

// Mode de développement
const DEV_MODE = false;

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const checkAdminAndRedirect = async () => {
      if (user) {
        try {
          const adminStatus = await isUserAdmin(user.uid, user.email || undefined);
          if (adminStatus) {
            // Si admin, rediriger vers le dashboard admin
            navigate('/admin/dashboard', { replace: true });
          } else {
            // Sinon, rediriger vers le dashboard normal
            navigate('/dashboard/profile', { replace: true });
          }
        } catch (error) {
          console.error('Erreur lors de la vérification admin:', error);
          // En cas d'erreur, rediriger vers le dashboard normal
          navigate('/dashboard/profile', { replace: true });
        }
      }
    };
    
    if (user) {
      checkAdminAndRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (DEV_MODE) {
      // Mode développement - authentification locale
      try {
        await localAuth.signIn(email, password);
        setSuccess('Connexion réussie ! Redirection...');
        setError('');
        // La redirection sera gérée par useEffect
      } catch (err: any) {
        setError(err.message || 'Email ou mot de passe incorrect');
        setSuccess('');
      }
      return;
    }
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // La redirection sera gérée par useEffect qui vérifie si admin
      setSuccess('Connexion réussie ! Redirection...');
    } catch (err) {
      setError('Email ou mot de passe incorrect');
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Veuillez entrer votre adresse email');
      return;
    }
    
    if (DEV_MODE) {
      try {
        await localAuth.resetPassword(email);
        setSuccess(`Mot de passe récupéré ! Vérifiez la console du navigateur (F12) pour voir votre mot de passe.`);
        setError('');
      } catch (err: any) {
        setError(err.message || 'Email non trouvé');
        setSuccess('');
      }
      return;
    }
    
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Un email de réinitialisation a été envoyé à votre adresse');
      setError('');
    } catch (err) {
      setError('Erreur lors de l\'envoi de l\'email de réinitialisation');
      setSuccess('');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3, color: '#a5816d' }}>
            Connexion
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Adresse email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{ color: '#a5816d' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 1,
                backgroundColor: '#a5816d',
                '&:hover': {
                  backgroundColor: '#8B7355',
                },
              }}
            >
              Se connecter
            </Button>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleForgotPassword}
                sx={{ color: '#a5816d', fontSize: '0.875rem' }}
              >
                J'ai oublié mon mot de passe
              </Link>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/register')}
                sx={{ color: '#a5816d' }}
              >
                Pas encore de compte ? Inscrivez-vous
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginScreen; 